/* GemSense: gs.js. One ES module on every page (deferred by type="module").
   §1 Core (sheets, quantity, tabs) · §2 Header · §3 Search · §4 Cart drawer · §5 Footer and
   page-wide overlays (chat, US notice) · §6 Rail.
   Each custom element is defined right after its class. */

/* §1 Core ================================================================ */

/* Sheets. [data-gs-open="id"] opens that <dialog class="gs-sheet"> with showModal(),
   which brings the focus trap, Escape, an inert page and the top layer; one at a time.
   [data-gs-close] or a click on the backdrop shuts it. */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const opener = event.target.closest('[data-gs-open]');
  if (opener) {
    const sheet = document.getElementById(opener.getAttribute('data-gs-open'));
    if (!(sheet instanceof HTMLDialogElement)) return;
    event.preventDefault();
    for (const open of document.querySelectorAll('dialog.gs-sheet[open]')) open.close();
    sheet.showModal();
    return;
  }

  const closer = event.target.closest('[data-gs-close]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }

  // A click on ::backdrop lands on the dialog itself, outside its box.
  const sheet = event.target;
  if (sheet instanceof HTMLDialogElement && sheet.classList.contains('gs-sheet')) {
    const box = sheet.getBoundingClientRect();
    const inside = event.clientX >= box.left && event.clientX <= box.right
      && event.clientY >= box.top && event.clientY <= box.bottom;
    if (!inside) sheet.close();
  }
});

/* Quantity: the buttons step the real input and tell its form. */
class GsQty extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#step);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#step);
  }

  #step = (event) => {
    const button = event.target instanceof Element && event.target.closest('[data-step]');
    const input = this.querySelector('input');
    if (!button || !input) return;
    const before = input.value;
    if (button.dataset.step === '1') input.stepUp();
    else input.stepDown();
    if (input.value !== before) input.dispatchEvent(new Event('change', { bubbles: true }));
  };
}
if (!customElements.get('gs-qty')) customElements.define('gs-qty', GsQty);

/* Tabs: click, or arrow keys / Home / End with a roving tabindex. */
class GsTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKey);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKey);
  }

  get #tabs() {
    return [...this.querySelectorAll(':scope > .gs-tabs__list > [role="tab"]')];
  }

  #select(chosen) {
    for (const tab of this.#tabs) {
      const on = tab === chosen;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    }
  }

  #onClick = (event) => {
    const tab = event.target instanceof Element && event.target.closest('[role="tab"]');
    if (tab && this.#tabs.includes(tab)) this.#select(tab);
  };

  #onKey = (event) => {
    const tabs = this.#tabs;
    const at = tabs.indexOf(document.activeElement);
    if (at === -1) return;
    const to = { ArrowRight: at + 1, ArrowLeft: at - 1, Home: 0, End: tabs.length - 1 }[event.key];
    if (to === undefined) return;
    event.preventDefault();
    const tab = tabs[(to + tabs.length) % tabs.length];
    tab.focus();
    this.#select(tab);
  };
}
if (!customElements.get('gs-tabs')) customElements.define('gs-tabs', GsTabs);

/* §2 Header ================================================================
   <gs-header> (snippets/gs-header.liquid). The category row: one panel open at a time, opened by
   hover (with a short delay so a passing mouse opens nothing) or click, shut by leaving the row
   and its panel, Escape or a click elsewhere. Vertical tab rails (the Bracelets panel, hovered or
   clicked; the phone menu, tapped; arrow keys in both). Its search fields open the search overlay
   (§3), and their placeholders cycle through example searches. The phone menu opens through §1. */

const HEADER_HINTS = ['pyrite bracelet', '7 mukhi rudraksh', 'evil eye bracelet', 'karungali mala', 'gift for wife'];

class GsHeader extends HTMLElement {
  #timer = 0;
  #hints = 0;
  #hint = 0;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKey);
    this.addEventListener('focusin', this.#onFocus);
    const desk = this.querySelector('.gs-hd__desk');
    desk?.addEventListener('mouseover', this.#onOver);
    desk?.addEventListener('mouseleave', this.#onLeave);
    document.addEventListener('click', this.#onDocClick);
    if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#hints = setInterval(this.#nextHint, 2800);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocClick);
    clearInterval(this.#hints);
    clearTimeout(this.#timer);
  }

  get #tops() {
    return [...this.querySelectorAll('[data-gs-hd-top]')];
  }

  /** Opens the panel of one category button (or none) and shuts the rest. */
  #open(button) {
    clearTimeout(this.#timer);
    for (const top of this.#tops) {
      const on = top === button;
      top.setAttribute('aria-expanded', String(on));
      const panel = document.getElementById(top.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    }
  }

  #select(tab, focus) {
    const list = tab.closest('[role="tablist"]');
    for (const t of list.querySelectorAll('[role="tab"]')) {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    }
    if (focus) tab.focus();
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const top = event.target.closest('[data-gs-hd-top]');
    if (top) {
      this.#open(top.getAttribute('aria-expanded') === 'true' ? null : top);
      return;
    }
    // §1 opens the menu sheet after this handler; then the sheet takes the focus, so its close
    // button does not show a focus ring the moment it opens.
    const opener = event.target.closest('[data-gs-open]');
    if (opener) setTimeout(() => document.getElementById(opener.dataset.gsOpen)?.focus());
    const tab = event.target.closest('[role="tab"]');
    if (tab) this.#select(tab);
    // The Search button with an empty field opens the overlay instead of an empty results page.
    const go = event.target.closest('.gs-hd-search__go');
    if (go && !go.form.q.value.trim()) {
      event.preventDefault();
      this.#search(go.form.q);
    }
  };

  #onOver = (event) => {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[data-gs-hd-hover] [role="tab"]');
    if (tab && tab.getAttribute('aria-selected') !== 'true') this.#select(tab);
    const top = event.target.closest('[data-gs-hd-top]');
    const plain = event.target.closest('[data-gs-hd-plain]');
    if (!top && !plain) {
      if (event.target.closest('.gs-hd__cats, .gs-hd-mega, .gs-hd-drop')) clearTimeout(this.#timer);
      else this.#onLeave();
      return;
    }
    if (top?.getAttribute('aria-expanded') === 'true') return;
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#open(top), 90);
  };

  #onLeave = () => {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#open(null), 220);
  };

  #onDocClick = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.#open(null);
  };

  #onKey = (event) => {
    if (event.key === 'Escape') {
      const top = this.#tops.find((t) => t.getAttribute('aria-expanded') === 'true');
      if (top) {
        this.#open(null);
        top.focus();
      }
      return;
    }
    const step = { ArrowDown: 1, ArrowUp: -1 }[event.key];
    const tab = event.target instanceof Element && event.target.closest('[role="tab"]');
    if (!step || !tab) return;
    event.preventDefault();
    const tabs = [...tab.closest('[role="tablist"]').querySelectorAll('[role="tab"]')];
    this.#select(tabs[(tabs.indexOf(tab) + step + tabs.length) % tabs.length], true);
  };

  #onFocus = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-gs-hd-input]')) {
      this.#search(event.target);
    }
  };

  /** Hands a header field over to the search overlay, which has the live results. */
  #search(input) {
    const modal = document.querySelector('gs-search-modal');
    const field = modal?.querySelector('[data-gs-input]');
    if (!field) return;
    input.closest('dialog')?.close(); // the menu sheet sits in the top layer, above the overlay
    input.blur();
    if (input.value.trim()) {
      field.value = input.value;
      field.dispatchEvent(new Event('input'));
    }
    modal.open();
  }

  #nextHint = () => {
    this.#hint = (this.#hint + 1) % HEADER_HINTS.length;
    const hint = HEADER_HINTS[this.#hint];
    for (const input of this.querySelectorAll('[data-gs-hd-input]')) {
      if (document.activeElement !== input) input.placeholder = `Search “${hint}”`;
    }
  };
}
if (!customElements.get('gs-header')) customElements.define('gs-header', GsHeader);

/* §3 Search ================================================================
   <gs-search-modal> (snippets/gs-search.liquid, rendered by sections/top-search.liquid): a
   centred overlay with its own input. Typing fetches the gs-search-results section through
   the storefront's predictive search. Any .gs-search-open (the header icon) opens it. */

const SEARCH_SECTION = 'gs-search-results';
const SEARCH_QUERY = 'resources[options][fields]=title,tag,vendor,product_type,variants.title,variants.sku'
  + '&resources[options][prefix]=last&resources[options][unavailable_products]=last'
  + `&resources[type]=query,product,collection,page,article&section_id=${SEARCH_SECTION}`;

class GsSearchModal extends HTMLElement {
  #timer = 0;
  #cache = new Map();

  connectedCallback() {
    for (const closer of this.querySelectorAll('[data-gs-close]')) {
      closer.addEventListener('click', () => this.close());
    }
    this.#input?.addEventListener('input', () => this.#onInput());
    this.querySelector('[data-gs-form]')?.addEventListener('submit', (event) => {
      if (!this.#input?.value.trim()) event.preventDefault();
    });
  }

  get #input() {
    return this.querySelector('[data-gs-input]');
  }

  open() {
    if (!this.hidden) return;
    this.hidden = false;
    void this.offsetHeight; // lay out once hidden is gone, so the opening transition runs
    this.classList.add('gs-search--open');
    document.documentElement.classList.add('gs-search-lock');
    document.addEventListener('keydown', this.#onKeydown);
    setTimeout(() => this.#input?.focus(), 60);
  }

  close() {
    if (this.hidden) return;
    this.classList.remove('gs-search--open');
    document.documentElement.classList.remove('gs-search-lock');
    document.removeEventListener('keydown', this.#onKeydown);
    setTimeout(() => { this.hidden = true; }, 220);
  }

  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'Tab') {
      this.#trapFocus(event);
    }
  };

  #trapFocus(event) {
    const card = this.querySelector('.gs-search__card');
    const focusable = [...card.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')]
      .filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  #onInput() {
    const term = this.#input.value.trim();
    clearTimeout(this.#timer);
    if (term.length < 2) {
      this.#show(null);
      this.#busy(false);
      return;
    }
    this.#busy(true);
    this.#timer = setTimeout(() => this.#fetch(term), 260);
  }

  /** null shows the guide (popular searches, stones, intentions); a string shows results. */
  #show(html) {
    const guide = this.querySelector('[data-gs-guide]');
    const results = this.querySelector('[data-gs-results]');
    if (guide) guide.hidden = html !== null;
    if (results) {
      results.innerHTML = html ?? '';
      results.hidden = html === null;
    }
  }

  #busy(on) {
    this.querySelector('[data-gs-spinner]')?.classList.toggle('is-active', on);
  }

  async #fetch(term) {
    if (this.#cache.has(term)) {
      this.#busy(false);
      this.#show(this.#cache.get(term));
      return;
    }
    const base = this.dataset.predictiveUrl || '/search/suggest';
    try {
      const response = await fetch(`${base}?q=${encodeURIComponent(term)}&${SEARCH_QUERY}`);
      if (!response.ok) throw new Error(String(response.status));
      const text = await response.text();
      if (this.#input.value.trim() !== term) return; // a newer term is on its way
      const section = new DOMParser().parseFromString(text, 'text/html')
        .querySelector(`#shopify-section-${SEARCH_SECTION}`);
      const html = section ? section.innerHTML : '';
      this.#cache.set(term, html);
      this.#busy(false);
      this.#show(html);
    } catch {
      this.#busy(false);
      this.#show('<div class="gs-search__empty"><span class="gs-search__empty-title">Search is unavailable right now.</span></div>');
    }
  }
}
if (!customElements.get('gs-search-modal')) customElements.define('gs-search-modal', GsSearchModal);

document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element && event.target.closest('.gs-search-open');
  if (!trigger) return;
  event.preventDefault();
  document.querySelector('gs-search-modal')?.open();
});

/* §4 Cart drawer: empty ===================================================
   Glozin's wishlist keeps product ids in localStorage. When it holds any, the
   empty cart shows those pieces instead of the best sellers, looked up through
   the storefront's own search suggestions (no template needed). Glozin swaps
   the drawer's innerHTML on every cart change, so this runs on each connect;
   lookups are cached per set of ids.
   Glozin only slides the drawer off-screen, so nothing is fetched (photos, lookup)
   until it first opens: Glozin then puts .open-minicart on <html>. The photos wait
   in <template>s (Chrome fetches a parsed lazy <img> even under display:none);
   reveal() stamps them and marks the element data-seen. */

const WISHLIST_KEY = 'glozin__wishlist-items';
const savedLookups = new Map();
let drawerSeen = false;
let drawerWatch = null;

function drawerIsOpen() {
  return document.documentElement.classList.contains('open-minicart');
}

/** One observer for the page, gone after the first open. */
function watchDrawer() {
  if (drawerWatch) return;
  drawerWatch = new MutationObserver(() => {
    if (!drawerIsOpen()) return;
    drawerSeen = true;
    drawerWatch.disconnect();
    for (const empty of document.querySelectorAll('gs-cart-empty:not([data-seen])')) empty.reveal();
    for (const saved of document.querySelectorAll('gs-cart-saved:not([data-seen])')) saved.refresh();
  });
  drawerWatch.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
}

function readWishlist() {
  try {
    const ids = JSON.parse(localStorage.getItem(WISHLIST_KEY) ?? '[]');
    return Array.isArray(ids) ? ids.map(String).filter((id) => /^\d+$/.test(id)) : [];
  } catch {
    return [];
  }
}

function lookUpProducts(ids) {
  const key = ids.join(',');
  if (!savedLookups.has(key)) {
    const base = window.routes?.predictive_search_url ?? '/search/suggest';
    const url = new URL(`${base}.json`, window.location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '4');
    // A string, not the URL object: an app's fetch wrapper (bluesky-toolkit.js) calls .includes() on it.
    const lookup = fetch(url.href)
      .then((response) => (response.ok ? response.json() : Promise.reject(response)))
      .then((data) => data.resources?.results?.products ?? [])
      .catch(() => {
        savedLookups.delete(key);
        return [];
      });
    savedLookups.set(key, lookup);
  }
  return savedLookups.get(key);
}

class GsCartEmpty extends HTMLElement {
  connectedCallback() {
    // The one-tap adds wait in <template>s: the drawer's <form> would swallow a parsed nested form.
    for (const add of this.querySelectorAll('template[data-empty-add]')) add.replaceWith(add.content);

    if (!this.hasAttribute('data-seen') && !drawerSeen && !drawerIsOpen()) {
      watchDrawer();
      return;
    }
    if (drawerIsOpen()) drawerSeen = true;
    this.reveal();
  }

  /** Saved pieces if the wishlist finds any; otherwise the best-seller photos go in. */
  async reveal() {
    this.setAttribute('data-seen', '');
    const ids = readWishlist();
    const pieces = ids.length > 0 ? await savedPieces(ids) : [];
    if (!this.isConnected) return;
    if (pieces.length > 0) {
      this.#showSaved(ids.length, pieces);
      return;
    }
    for (const photo of this.querySelectorAll('template[data-empty-img]')) photo.replaceWith(photo.content);
  }

  #showSaved(count, pieces) {
    const saved = this.querySelector('[data-empty-saved]');
    const start = this.querySelector('[data-empty-start]');
    if (!saved || !start) return;

    saved.querySelector('[data-empty-count]').textContent = count === 1 ? '1 piece' : `${count} pieces`;
    saved.querySelector('[data-saved-rows]').replaceChildren(...pieces.map(savedRow));

    start.hidden = true;
    saved.hidden = false;
    this.querySelector('[data-empty-browse]')?.setAttribute('hidden', '');
    this.querySelector('[data-empty-see]')?.removeAttribute('hidden');
  }
}
if (!customElements.get('gs-cart-empty')) customElements.define('gs-cart-empty', GsCartEmpty);

/* Saved for later in a cart that has pieces (<gs-cart-saved>, gs-cart-perks zone 'saved'): the
   wishlist's pieces that are not in the cart. Waits for the drawer's first open, as above. */
class GsCartSaved extends HTMLElement {
  connectedCallback() {
    if (!drawerSeen && !drawerIsOpen()) {
      watchDrawer();
      return;
    }
    if (drawerIsOpen()) drawerSeen = true;
    this.refresh();
  }

  async refresh() {
    this.setAttribute('data-seen', '');
    const inCart = (this.dataset.inCart ?? '').split(',');
    const ids = readWishlist().filter((id) => !inCart.includes(id));
    const pieces = ids.length > 0 ? await savedPieces(ids) : [];
    if (!this.isConnected) return;
    this.querySelector('[data-saved-rows]')?.replaceChildren(...pieces.map(savedRow));
    this.hidden = pieces.length === 0;
  }
}
if (!customElements.get('gs-cart-saved')) customElements.define('gs-cart-saved', GsCartSaved);

/* A saved piece needs its variant to be added, and the search lookup has none: each piece's
   own JSON is fetched once and cached per handle. */
const productLookups = new Map();

function productJson(handle) {
  if (!productLookups.has(handle)) {
    const lookup = fetch(`/products/${encodeURIComponent(handle)}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    productLookups.set(handle, lookup);
  }
  return productLookups.get(handle);
}

async function savedPieces(ids) {
  const products = await lookUpProducts(ids);
  return Promise.all(products.map(async (product) => ({ product, full: await productJson(product.handle) })));
}

/** The shop's money format without trailing zeros (as Liquid's money_without_trailing_zeros): ₹ 799. */
function formatMoney(cents) {
  const format = window.themeGlobalVariables?.settings?.money_format ?? '₹ {{amount}}';
  const amount = (cents / 100).toLocaleString('en-US', { maximumFractionDigits: 2 });
  return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
}

/** The cart's short title: the name before "|" or " for ". */
function shortTitle(title) {
  return String(title).split('|')[0].split(/ for /i)[0].trim();
}

/** One saved piece: photo, name, price and Add to cart (or View, when it has choices or is sold out).
    Text goes in as text, never HTML. */
function savedRow({ product, full }) {
  const title = shortTitle(product.title);
  const row = document.createElement('li');
  row.className = 'gs-saved__row';

  const photo = document.createElement('span');
  photo.className = 'gs-saved__img';
  if (product.image) {
    const src = new URL(product.image, window.location.href);
    src.searchParams.set('width', '120');
    const image = document.createElement('img');
    image.src = src.href;
    image.alt = '';
    image.width = 48;
    image.height = 48;
    image.loading = 'lazy';
    photo.append(image);
  }

  const body = document.createElement('a');
  body.className = 'gs-saved__bd';
  body.href = product.url;
  const name = document.createElement('span');
  name.className = 'gs-saved__t';
  name.textContent = title;
  const price = document.createElement('span');
  price.className = 'gs-saved__p';
  const cents = full?.price ?? Math.round(Number(product.price) * 100);
  price.textContent = formatMoney(cents);
  const compare = full?.compare_at_price ?? 0;
  if (compare > cents) {
    const was = document.createElement('s');
    was.textContent = formatMoney(compare);
    price.append(was);
  }
  body.append(name, price);

  const variants = full?.variants ?? [];
  let action;
  if (variants.length === 1 && variants[0].available) {
    action = document.createElement('button');
    action.type = 'button';
    action.dataset.gsSavedAdd = String(variants[0].id);
    action.setAttribute('aria-label', `Add ${title} to cart`);
    action.textContent = 'Add to cart';
  } else {
    action = document.createElement('a');
    action.href = product.url;
    action.textContent = 'View';
  }
  action.className = 'gs-saved__add';

  row.append(photo, body, action);
  return row;
}

/* Cart drawer: coupon copy, quantity guard and quantity at once, adds in place (perk, saved
   piece, undo), remove with undo, the Added bar, ?open_cart=1 ============================
   Glozin (cart-components.js) replaces #minicart-form's innerHTML on every cart change,
   so everything here listens on document and never holds a drawer node. */

/* [data-gs-copy] (gs-cart-perks, a type="button": in some drawer states the coupon sits
   inside the cart form, where a submit button would check out). */
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element && event.target.closest('[data-gs-copy]');
  if (!button) return;
  event.preventDefault();
  const code = button.getAttribute('data-gs-copy');
  if (!code) return;
  // navigator.clipboard is missing on insecure origins and can reject when the document is not
  // focused, so the textarea copy is a real fallback.
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(code).then(
      () => flashCopied(button),
      () => { if (copyWithTextarea(code)) flashCopied(button); },
    );
  } else if (copyWithTextarea(code)) {
    flashCopied(button);
  }
});

const copiedTimers = new WeakMap();

function flashCopied(button) {
  button.setAttribute('data-copied', 'true');
  clearTimeout(copiedTimers.get(button));
  copiedTimers.set(button, setTimeout(() => button.removeAttribute('data-copied'), 1800));
}

function copyWithTextarea(text) {
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.cssText = 'position:fixed;top:-1000px;left:-1000px;opacity:0';
  document.body.append(area);
  area.select();
  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }
  area.remove();
  return copied;
}

/* The quantity input has min="0", so minus at 1 would delete the line with no warning and no
   undo. At 1 the minus is dimmed (is-min, aria-disabled) and does nothing; removing is the bin
   in the card's corner. */
const STEPPER = '.minicart__wrapper quantity-input';

function stepperQuantity(stepper) {
  return parseInt(stepper.querySelector('input')?.value, 10) || 0;
}

function paintSteppers() {
  for (const stepper of document.querySelectorAll(STEPPER)) {
    const minus = stepper.querySelector('[name="minus"]');
    if (!minus) continue;
    const atMin = stepperQuantity(stepper) <= 1;
    minus.classList.toggle('is-min', atMin);
    minus.setAttribute('aria-disabled', String(atMin));
    if (atMin) minus.setAttribute('title', 'Use the bin to remove this item');
    else minus.removeAttribute('title');
  }
}

// Capture phase, so this runs before Glozin's own handler on the button.
document.addEventListener('click', (event) => {
  const minus = event.target instanceof Element && event.target.closest(`${STEPPER} [name="minus"]`);
  const stepper = minus && minus.closest('quantity-input');
  if (stepper && stepperQuantity(stepper) <= 1) {
    event.preventDefault();
    event.stopPropagation();
  }
}, true);

document.addEventListener('change', paintSteppers);
document.addEventListener('input', paintSteppers);
// A poll on purpose: one quantity update re-renders the drawer more than once, and Glozin sets
// input.value as a property (no event), so every event-timed repaint lost the race. It only
// paints, and only while the drawer is open (Glozin puts open-minicart on <html>), painting at
// once on opening; the guard above reads the live value at click time.
let stepperPoll = 0;
new MutationObserver(() => {
  const open = document.documentElement.classList.contains('open-minicart');
  if (open && !stepperPoll) {
    paintSteppers();
    stepperPoll = setInterval(paintSteppers, 700);
  } else if (!open && stepperPoll) {
    clearInterval(stepperPoll);
    stepperPoll = 0;
  }
}).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
paintSteppers();

/* Adds in place: /cart/add.js with the minicart-form section, the drawer re-rendered from it
   (Glozin's own way), the counts refreshed and cart:updated sent, as Glozin's own changes do.
   Throws when the cart says no. Used by a perk's Add, Undo and a saved piece's Add to cart. */
async function addToDrawer(items) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items, sections: 'minicart-form', sections_url: window.location.pathname }),
  });
  if (!response.ok) throw new Error('add failed');
  const html = (await response.json()).sections?.['minicart-form'];
  const holder = document.getElementById('minicart-form');
  const inner = html && new DOMParser().parseFromString(html, 'text/html').querySelector('#minicart-form');
  if (holder && inner) holder.innerHTML = inner.innerHTML;
  try {
    window.BlsLazyloadImg?.init?.();
  } catch {
    // Glozin's lazy loader is optional here: the images still load when scrolled to.
  }
  document.querySelector('cart-notification')?.cartAction?.();
  const cart = await (await fetch('/cart.js')).json();
  for (const count of document.querySelectorAll('.cart-count')) {
    count.textContent = count.classList.contains('cart-count-drawer') ? ` (${cart.item_count}) ` : cart.item_count;
  }
  document.dispatchEvent(new CustomEvent('cart:updated'));
}

/* A perk's "Add" ([data-gs-ajax-add] in gs-cart-perks). On any failure the link's own href takes
   over: a full-page add that comes back with ?open_cart=1. Capture phase: the drawer stops
   bubbling clicks. */
document.addEventListener('click', async (event) => {
  const link = event.target instanceof Element && event.target.closest('[data-gs-ajax-add]');
  if (!link) return;
  event.preventDefault();
  event.stopPropagation(); // the re-render takes the link out: see the saved piece's Add to cart
  if (link.dataset.gsBusy) return;
  link.dataset.gsBusy = '1';
  link.style.opacity = '.55';
  link.style.pointerEvents = 'none';
  link.textContent = 'Adding…';
  try {
    await addToDrawer([{ id: parseInt(link.dataset.vid, 10), quantity: 1 }]);
  } catch {
    window.location.href = link.getAttribute('href');
  }
}, true);

/* A saved piece's Add to cart (savedRow): adds in place, no page load, then the Added bar.
   The click stops here: the re-render takes the button out of the page, and Glozin's
   click-outside check would then find it outside the drawer and close it. */
document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element && event.target.closest('[data-gs-saved-add]');
  if (!button) return;
  event.preventDefault();
  event.stopPropagation();
  if (button.getAttribute('aria-busy') === 'true') return;
  button.setAttribute('aria-busy', 'true');
  try {
    await addToDrawer([{ id: Number(button.dataset.gsSavedAdd), quantity: 1 }]);
    showAdded();
  } catch {
    button.removeAttribute('aria-busy');
    const row = button.closest('.gs-saved__row');
    if (row && !row.querySelector('.gs-saved__error')) {
      const note = document.createElement('p');
      note.className = 'gs-saved__error';
      note.setAttribute('role', 'alert');
      note.textContent = 'Couldn’t add it. Try again.';
      row.append(note);
    }
  }
}, true);

/* Remove, with undo. The bin removes the line at once (Glozin's updateQuantity to 0) instead of
   Glozin's question first; the line's place then holds "… removed · Undo · Save to wishlist"
   for 6 s (it survives the drawer's re-renders: cart:updated puts it back). Undo adds the line
   back exactly (gs-cart-line's data: variant, quantity, properties). Capture phase, before
   Glozin's own handler on the bin. */
const UNDO_MS = 6000;
let pendingUndo = null;

function clearUndo() {
  if (!pendingUndo) return;
  clearTimeout(pendingUndo.timer);
  pendingUndo = null;
  document.querySelector('#minicart_wrapper .gs-undo')?.remove();
}

function saveToWishlist(productId) {
  const ids = readWishlist();
  const id = String(productId);
  if (!ids.includes(id)) ids.push(id);
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(ids));
  } catch {
    // Storage can be blocked (private mode); the piece is simply not saved.
  }
}

function undoRow(line, left) {
  const row = document.createElement('div');
  row.className = 'gs-undo';
  row.setAttribute('role', 'status');
  if (line.image) {
    const image = document.createElement('img');
    image.className = 'gs-undo__img';
    image.src = line.image;
    image.alt = '';
    image.width = 40;
    image.height = 40;
    row.append(image);
  }
  const text = document.createElement('p');
  text.className = 'gs-undo__t';
  const name = document.createElement('b');
  name.textContent = line.title;
  text.append(name, ' removed');

  const actions = document.createElement('div');
  actions.className = 'gs-undo__acts';
  const undo = document.createElement('button');
  undo.type = 'button';
  undo.className = 'gs-undo__btn';
  undo.dataset.gsUndo = '';
  undo.textContent = 'Undo';
  actions.append(undo);
  if (!readWishlist().includes(String(line.product))) {
    const save = document.createElement('button');
    save.type = 'button';
    save.className = 'gs-undo__save';
    save.dataset.gsUndoSave = '';
    save.textContent = 'Save to wishlist';
    actions.append(save);
  }

  const bar = document.createElement('span');
  bar.className = 'gs-undo__bar';
  bar.style.animationDuration = `${left}ms`;
  row.append(text, actions, bar);
  return row;
}

/** Puts the row where the line was (or above the empty cart), unless it is already there. */
function showUndo() {
  if (!pendingUndo || document.querySelector('#minicart_wrapper .gs-undo')) return;
  const left = pendingUndo.until - Date.now();
  if (left <= 0) {
    clearUndo();
    return;
  }
  const row = undoRow(pendingUndo.line, left);
  const list = document.querySelector('#minicart_wrapper .items');
  if (list) {
    const cards = [...list.querySelectorAll(':scope > .cart-item')];
    list.insertBefore(row, cards[pendingUndo.index] ?? null);
  } else {
    document.querySelector('#minicart_wrapper #minicart')?.prepend(row);
  }
}
document.addEventListener('cart:updated', showUndo);

document.addEventListener('click', (event) => {
  const bin = event.target instanceof Element && event.target.closest('#minicart_wrapper mini-cart-remove-button');
  if (!bin) return;
  const cart = bin.closest('cart-notification');
  const card = bin.closest('.cart-item');
  let line = null;
  try {
    line = JSON.parse(card?.querySelector('[data-gs-line]')?.textContent ?? 'null');
  } catch {
    line = null;
  }
  if (!line || typeof cart?.updateQuantity !== 'function') return; // Glozin's own remove takes over
  event.preventDefault();
  event.stopPropagation();
  if (bin.classList.contains('loading')) return;

  clearUndo();
  const cards = [...card.parentElement.querySelectorAll(':scope > .cart-item')];
  pendingUndo = { line, index: cards.indexOf(card), until: Date.now() + UNDO_MS };
  pendingUndo.timer = setTimeout(clearUndo, UNDO_MS);
  cart.updateQuantity(bin.dataset.index, 0);
}, true);

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const undo = target?.closest('[data-gs-undo]');
  const save = target?.closest('[data-gs-undo-save]');
  if (!undo && !save) return;
  event.preventDefault();
  event.stopPropagation(); // the row is replaced: see the saved piece's Add to cart above
  const pending = pendingUndo;
  const row = (undo ?? save).closest('.gs-undo');
  if (!pending || !row) return;
  clearTimeout(pending.timer);
  pendingUndo = null;

  if (save) {
    saveToWishlist(pending.line.product);
    const done = document.createElement('p');
    done.className = 'gs-undo__t';
    done.textContent = 'Saved to your wishlist';
    row.replaceChildren(done);
    setTimeout(() => row.remove(), 2000);
    for (const saved of document.querySelectorAll('gs-cart-saved')) saved.refresh();
    return;
  }

  row.setAttribute('aria-busy', 'true');
  const { id, quantity, properties } = pending.line;
  const item = { id, quantity };
  // Liquid writes a line without properties as [], which /cart/add.js refuses.
  if (properties && !Array.isArray(properties) && Object.keys(properties).length > 0) item.properties = properties;
  try {
    await addToDrawer([item]);
  } catch {
    // The row stays, with the reason, for another try.
    row.removeAttribute('aria-busy');
    row.querySelector('.gs-undo__t').textContent = 'Couldn’t put it back. Try again.';
    pending.until = Date.now() + UNDO_MS;
    pending.timer = setTimeout(clearUndo, UNDO_MS);
    pendingUndo = pending;
  }
}, true);

/* Quantity answers at once. Glozin waits 300 ms, then the cart, before the number changes; so
   on the tap the new number (and line total) shows straight away, the line is busy (stepper
   resting, a spinner) and the drawer is busy (subtotal "Updating…", checkout waits). A second
   tap while busy does nothing. Glozin's re-render ends it; if the cart says no, Glozin puts the
   input back, its bin's loading class goes, and the line shows what the cart holds again. */
function lineData(card) {
  try {
    return JSON.parse(card.querySelector('[data-gs-line]')?.textContent ?? 'null');
  } catch {
    return null;
  }
}

function paintLine(card, quantity) {
  const number = card.querySelector('.minicart__qty-num');
  if (number) number.textContent = String(quantity).padStart(2, '0');
  const total = card.querySelector('[data-gs-line-total]');
  const line = lineData(card);
  if (total && line) {
    total.hidden = quantity < 2;
    total.textContent = `${quantity} × ${formatMoney(line.price)} = ${formatMoney(line.price * quantity)}`;
  }
}

function settleDrawer() {
  const drawer = document.getElementById('minicart_wrapper');
  if (drawer && !drawer.querySelector('.cart-item[aria-busy="true"]')) drawer.removeAttribute('data-gs-busy');
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented) return; // the at-1 guard above took it
  const button = event.target instanceof Element && event.target.closest(`${STEPPER} .quantity__button`);
  const card = button && button.closest('.cart-item');
  if (!card) return;
  if (card.getAttribute('aria-busy') === 'true') {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  // Glozin's quantity-input steps the input in this same click; read it once that has run.
  setTimeout(() => {
    const input = card.querySelector('input[name="updates[]"]');
    const quantity = parseInt(input?.value, 10);
    if (!quantity || String(quantity) === input.dataset.value) return;
    card.setAttribute('aria-busy', 'true');
    paintLine(card, quantity);
    document.getElementById('minicart_wrapper')?.setAttribute('data-gs-busy', '');
  }, 0);
}, true);

document.addEventListener('cart:updated', settleDrawer);

const minicartRoot = document.getElementById('minicart_wrapper');
if (minicartRoot) {
  new MutationObserver((records) => {
    for (const { target } of records) {
      if (!(target instanceof Element) || target.localName !== 'mini-cart-remove-button') continue;
      if (target.classList.contains('loading')) continue;
      const card = target.closest('.cart-item[aria-busy="true"]');
      if (!card) continue;
      card.removeAttribute('aria-busy');
      paintLine(card, parseInt(card.querySelector('input[name="updates[]"]')?.value, 10) || 1);
    }
    settleDrawer();
  }).observe(minicartRoot, { subtree: true, attributes: true, attributeFilter: ['class'] });
}

/* "Added to your cart": a slim bar under the drawer's header for 3 s after an add. A product
   form's submit leaves a note in sessionStorage (a phone add goes through /cart and comes back
   with ?open_cart=1); the drawer's next re-render, or that return, shows the bar. The note is
   dropped when the drawer closes (Glozin closes it when an add fails) or after 20 s. */
const ADDED_KEY = 'gs-cart-added';
let addedTimer = 0;

function takeAddedNote() {
  let at = 0;
  try {
    at = Number(sessionStorage.getItem(ADDED_KEY));
    sessionStorage.removeItem(ADDED_KEY);
  } catch {
    at = 0;
  }
  return at > 0 && Date.now() - at < 20000;
}

function showAdded() {
  const head = document.querySelector('#minicart_wrapper .minicart__header');
  if (!head) return;
  let bar = document.querySelector('#minicart_wrapper .gs-added');
  if (!bar) {
    bar = document.createElement('p');
    bar.className = 'gs-added';
    bar.setAttribute('role', 'status');
    bar.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7"/></svg>Added to your cart';
    head.after(bar);
  }
  clearTimeout(addedTimer);
  addedTimer = setTimeout(() => bar.remove(), 3000);
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) return;
  try {
    sessionStorage.setItem(ADDED_KEY, String(Date.now()));
  } catch {
    // No storage: no bar, the add itself is unaffected.
  }
}, true);

const minicartForm = document.getElementById('minicart-form');
if (minicartForm) {
  new MutationObserver(() => {
    if (takeAddedNote()) showAdded();
  }).observe(minicartForm, { childList: true });
}

let drawerWasOpen = drawerIsOpen();
new MutationObserver(() => {
  const open = drawerIsOpen();
  if (drawerWasOpen && !open) {
    try {
      sessionStorage.removeItem(ADDED_KEY);
    } catch {
      // Nothing stored.
    }
  }
  drawerWasOpen = open;
}).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

/* ?open_cart=1 (a full-page perk add, or sections/main-cart-items.liquid) opens the drawer once
   and drops the parameter from the address bar. Only where the drawer exists (not on /cart). */
const cartDrawer = document.getElementById('minicart_wrapper');
if (cartDrawer && window.location.search.includes('open_cart=1')) {
  history.replaceState(null, '', window.location.pathname);
  setTimeout(() => {
    cartDrawer.querySelector('.minicart__wrapper')?.classList.add('open');
    cartDrawer.classList.add('open');
    setTimeout(() => document.documentElement.classList.add('open-drawer', 'open-minicart'), 100);
    if (takeAddedNote()) showAdded();
  }, 100);
}

/* §5 Footer and page-wide overlays ========================================= */

/* <gs-footer-menus> (sections/footer.liquid): each menu is a native <details>, open from
   768px up and folded below it; <details> brings keyboard use and works without script. */
class GsFooterMenus extends HTMLElement {
  #wide = window.matchMedia('(min-width: 768px)');

  connectedCallback() {
    this.#sync();
    this.#wide.addEventListener('change', this.#sync);
  }

  disconnectedCallback() {
    this.#wide.removeEventListener('change', this.#sync);
  }

  #sync = () => {
    for (const menu of this.querySelectorAll('.gs-footer__menu')) menu.open = this.#wide.matches;
  };
}
if (!customElements.get('gs-footer-menus')) customElements.define('gs-footer-menus', GsFooterMenus);

/* <gs-us-notice> (snippets/gs-us-notice.liquid): only for a browser whose time zone is a US one,
   at most once in 14 days, after 30% of the page or 25 seconds. No network calls. */
const US_ZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu', 'America/Detroit',
  'America/Indiana/Indianapolis', 'America/Kentucky/Louisville', 'America/Boise', 'America/Juneau',
  'America/Sitka', 'America/Nome', 'America/Adak', 'America/Menominee', 'America/North_Dakota/Center',
  'America/Indiana/Vincennes', 'America/Indiana/Knox', 'America/Puerto_Rico'];
const US_NOTICE_KEY = 'gs_usn_dismissed';
const US_NOTICE_DAYS = 14;

class GsUsNotice extends HTMLElement {
  #shown = false;

  connectedCallback() {
    try {
      const last = localStorage.getItem(US_NOTICE_KEY);
      if (last && Date.now() - parseInt(last, 10) < US_NOTICE_DAYS * 864e5) return;
    } catch {
      return;
    }
    let zone = '';
    try {
      zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
      zone = '';
    }
    if (!US_ZONES.includes(zone)) return;

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    setTimeout(this.#show, 25000);
    for (const button of this.querySelectorAll('[data-usn-close]')) button.addEventListener('click', this.#dismiss);
    this.querySelector('.gs-usn-cta')?.addEventListener('click', () => this.#remember());
  }

  #onScroll = () => {
    const root = document.documentElement;
    const max = root.scrollHeight - root.clientHeight;
    if (max > 0 && root.scrollTop / max > 0.3) this.#show();
  };

  #show = () => {
    if (this.#shown) return;
    this.#shown = true;
    this.hidden = false;
    this.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => this.classList.add('gs-usn-show'));
    window.removeEventListener('scroll', this.#onScroll);
  };

  #dismiss = () => {
    this.classList.remove('gs-usn-show');
    setTimeout(() => { this.hidden = true; }, 350);
    this.#remember();
  };

  #remember() {
    try {
      localStorage.setItem(US_NOTICE_KEY, String(Date.now()));
    } catch {
      // Private mode: the notice may come back next visit, nothing breaks.
    }
  }
}
if (!customElements.get('gs-us-notice')) customElements.define('gs-us-notice', GsUsNotice);

/* <gs-chat> (snippets/gs-chat.liquid): the round button opens the guidance panel; a click
   anywhere outside the button and the panel closes it. */
class GsChat extends HTMLElement {
  connectedCallback() {
    document.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKey);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKey);
  }

  #setOpen(open) {
    const button = this.querySelector('.gs-chat-btn');
    const panel = this.querySelector('.gs-chat-box');
    if (!button || !panel) return;
    panel.hidden = !open;
    button.setAttribute('aria-expanded', String(open));
  }

  #onClick = (event) => {
    const button = this.querySelector('.gs-chat-btn');
    const panel = this.querySelector('.gs-chat-box');
    if (!button || !panel || !(event.target instanceof Node)) return;
    if (button.contains(event.target)) this.#setOpen(panel.hidden);
    else if (!panel.contains(event.target)) this.#setOpen(false);
  };

  // The button is a div with role="button": Enter and Space open it, Escape closes the panel.
  #onKey = (event) => {
    const button = this.querySelector('.gs-chat-btn');
    const panel = this.querySelector('.gs-chat-box');
    if (!button || !panel) return;
    if (event.target === button && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.#setOpen(panel.hidden);
    } else if (event.key === 'Escape' && !panel.hidden) {
      this.#setOpen(false);
      button.focus();
    }
  };
}
if (!customElements.get('gs-chat')) customElements.define('gs-chat', GsChat);

// Media that waits for the page: video posters (video[data-poster], home's shoppable video) and
// hidden-menu photos (img[data-gs-src], the mega panel). They are set once the page has loaded, so
// they don't compete with the page's own photos for the connection. Card videos
// (.lazy-video-item) are left alone: utils.js initLazyloadItem sets their src only while
// data-poster is still there.
const gsLoadDeferred = () => {
  document.querySelectorAll('video[data-poster]:not(.lazy-video-item)').forEach((video) => {
    video.poster = video.dataset.poster;
    video.removeAttribute('data-poster');
  });
  document.querySelectorAll('img[data-gs-src]').forEach((img) => {
    img.src = img.dataset.gsSrc;
    img.removeAttribute('data-gs-src');
  });
};
if (document.readyState === 'complete') gsLoadDeferred();
else window.addEventListener('load', gsLoadDeferred, { once: true });

/* §6 Rail ================================================================
   Arrows page the track by 90% of its width; disabled at each end, gone when
   nothing overflows (data-static). */

class GsRail extends HTMLElement {
  #track = null;
  #observer = null;

  connectedCallback() {
    this.#track = this.querySelector('.gs-rail__track');
    if (!this.#track) return;
    this.#track.addEventListener('scroll', this.#paint, { passive: true });
    this.addEventListener('click', this.#move);
    this.#observer = new ResizeObserver(this.#paint);
    this.#observer.observe(this.#track);
  }

  disconnectedCallback() {
    this.#track?.removeEventListener('scroll', this.#paint);
    this.removeEventListener('click', this.#move);
    this.#observer?.disconnect();
  }

  #paint = () => {
    const { scrollLeft, scrollWidth, clientWidth } = this.#track;
    this.toggleAttribute('data-static', scrollWidth <= clientWidth + 1);
    const prev = this.querySelector('[data-rail="-1"]');
    const next = this.querySelector('[data-rail="1"]');
    if (prev) prev.disabled = scrollLeft <= 1;
    if (next) next.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
  };

  #move = (event) => {
    const button = event.target instanceof Element && event.target.closest('[data-rail]');
    if (!button) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollBy({
      left: Number(button.dataset.rail) * this.#track.clientWidth * 0.9,
      behavior: still ? 'auto' : 'smooth',
    });
  };
}
if (!customElements.get('gs-rail')) customElements.define('gs-rail', GsRail);
